import { useEffect, useRef, useState } from 'react';
import { appMode, selectNavigation, useAppDispatch, useAppSelector } from '@state';
import { useAppToast, useDesign } from '@hooks';
import { lang } from '@lang';
import { featureHook } from '@features/feature-hook';
import { Feature } from '@features/feature.type';
import { Link2 } from 'lucide-react';
import { saveCustomerEstimate } from '@external';
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { useGenerateEstimateBlob } from './generate-estimate-blob';


export const useEstimateLinkFeature = (): Feature<null> => {
    const [controller] = useDesign();
    const dispatch = useAppDispatch();
    const { getToken } = useKeystoneSession();

    const appToast = useAppToast();
    const toastCtrlRef = useRef<ReturnType<typeof appToast.loading> | null>(null);

    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const appNav = useAppSelector(selectNavigation);

    const getEstimateBlob = useGenerateEstimateBlob();

    const isDraft = controller?.kitchen?.version === null;

    const fnSuccess = (pdfUrl: string): void => {
            if (controller && pdfUrl && appNav.email) {
				const newUrl = pdfUrl;

                const copyToClipboard = async (text: string): Promise<boolean> => {
                    // Try modern clipboard API first
                    if (navigator.clipboard && document.hasFocus()) {
                        try {
                            await navigator.clipboard.writeText(text);
                            return true;
                        } catch (err) {
                            console.warn('Clipboard API failed, trying fallback:', err);
                        }
                    }

                    return false;
                };

                copyToClipboard(newUrl).then((clipboardSuccess) => {
                    if (clipboardSuccess) {
                        toastCtrlRef.current?.success({
                            title: 'Link Copied',
                            description: 'A public link to the estimate PDF has been copied to your clipboard.'
                        });
                    } else {
                        toastCtrlRef.current?.warning({
                            title: 'Link Generated',
                            description: `Could not auto-copy. Link: ${newUrl}`
                        });
                    }
                });
            }
        };

        useEffect(() => {
            if (error !== null) {
                toastCtrlRef.current?.error({ title: lang.customer.message.saveFailed });
            }
        }, [error]);

    const [disabled, tooltip] = (() => {
		if (!controller) return [true, lang.customer.tooltip.noController];
		if (isDraft) {
			// Draft without changes - can still save as new version
			return [false, lang.customer.tooltip.saveDraftAsNewVersion];
		}

		return [true, lang.customer.tooltip.notDirty];
	})();


	return featureHook<null>({
		name: 'saveEstimateFeature',
		label: 'Get Estimate Link',
		shortcut: '',
		tooltip: 'Saves the current estimate and copies a shareable link to your clipboard.',
		icon: (props = {}) => <Link2 {...props} />,
		disabled,
		value: null,
		hidden: appNav.appMode === appMode.view,
		handler: async () => {
			if (controller && appNav.email) {
				toastCtrlRef.current = appToast.loading({ title: 'Generating Estimate Link...' });

				let estimateBlob: Blob;
				try {
					estimateBlob = await getEstimateBlob();
					console.log('estimate-data', estimateBlob);
				} catch (err) {
					console.error('Failed to generate estimate:', err);
					toastCtrlRef.current.error({
						title: 'Failed to generate estimate',
						description: err instanceof Error ? err.message : 'Unknown error occurred'
					});
					return;
				}

				const blueprint = controller.kitchen?.context?.blueprint;
				const blueprintId = blueprint?.submissionId ?? blueprint?.id;

				if (!blueprintId) {
					toastCtrlRef.current.error({
						title: 'Error',
						description: 'Could not determine blueprint ID'
					});
					return;
				}

				// Save (either as draft or as new version/saved version)
				const designLabel = controller.kitchen?.description ?? 'Untitled';
				saveCustomerEstimate({
                    controller,
                    email: appNav.email,
                    blueprintId,
                    designLabel,
                    mode: 'live',
                    getToken,
                    setSuccess: (successUrl: string) => {
                        setSuccess(successUrl);
                        fnSuccess(successUrl);
                    },
                    setError: (err: string) => {
                        setError(err);
                    },
                    estimate: estimateBlob
                });
			}
		}
	});
}
